import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Archive, Loader2, Pencil, Search, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { request } from "../lib/api";
import { useAttribution } from "../lib/useAttribution";
import { useRowSelection } from "../lib/useRowSelection";
import { AttributionCell } from "../components/Attribution";
import type { CreatedResponse, Notice, PDDay, PDDaysResponse, StructureResponse } from "../types";
import {
  Badge,
  BulkActionsBar,
  Card,
  ConfirmModal,
  EmptyState,
  ErrorState,
  Field,
  InlineNotice,
  LoadingState,
  Table,
  errorMessage,
  inputClass,
  labelClass,
  primaryButtonClass,
} from "../components/ui";

// PD days are authored here: each one holds one or more modules, and each
// module holds content. Scheduling a PD day at a centre is a separate step
// (Schedule), so the same PD day can run at several centres.
export function PDDays() {
  const navigate = useNavigate();
  const [days, setDays] = useState<PDDay[] | null>(null);
  const [listError, setListError] = useState("");
  const [message, setMessage] = useState<Notice | null>(null);
  const [busy, setBusy] = useState(false);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [cohorts, setCohorts] = useState<{ id: string; name: string }[]>([]);
  const [deletingID, setDeletingID] = useState<string | null>(null);
  const [confirmDay, setConfirmDay] = useState<PDDay | null>(null);
  const [confirmAction, setConfirmAction] = useState<"archive" | "delete">("archive");
  const attribution = useAttribution(
    "pd-days",
    (days ?? []).map((day) => day.id)
  );
  const load = useCallback(() => {
    request<PDDaysResponse>("/api/admin/pd-days")
      .then((data) => {
        setDays(data.pd_days);
        setListError("");
      })
      .catch((cause) => setListError(errorMessage(cause)));
  }, []);
  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    request<StructureResponse>("/api/admin/structure/cohorts")
      .then((data) => setCohorts(data.items.map((item) => ({ id: item.id, name: item.name }))))
      .catch(() => setCohorts([]));
  }, []);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    const form = event.currentTarget;
    const data = Object.fromEntries(new FormData(form));
    const payload = {
      cohort_id: data.cohort_id,
      title: data.title,
      description: data.description,
      status: data.status,
      translations: { ur: { title: data.title_ur, description: "" } },
    };
    try {
      const result = await request<CreatedResponse>("/api/admin/pd-days", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      // Straight on to the new PD day, where its modules are added.
      navigate(`/pd-days/${result.id}`);
    } catch (cause) {
      setMessage({ tone: "error", text: errorMessage(cause) });
    } finally {
      setBusy(false);
    }
  }
  async function remove(day: PDDay, action: "archive" | "delete") {
    setDeletingID(day.id);
    setMessage(null);
    try {
      await request(`/api/admin/pd-days/${day.id}${action === "delete" ? "/permanent" : ""}`, {
        method: "DELETE",
      });
      setMessage({
        tone: "success",
        text: action === "delete" ? "PD day deleted." : "PD day archived.",
      });
      load();
    } catch (cause) {
      setMessage({ tone: "error", text: errorMessage(cause) });
    } finally {
      setDeletingID(null);
      setConfirmDay(null);
    }
  }
  const filteredDays = (days || []).filter(
    (day) =>
      [day.title, day.description, day.cohort, day.status]
        .filter((value): value is string => Boolean(value))
        .some((value) => value.toLowerCase().includes(query.toLowerCase())) &&
      (statusFilter === "all" || day.status === statusFilter)
  );
  const { selection, selectedIds, clear } = useRowSelection(filteredDays.map((day) => day.id));
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [confirmingBulkDelete, setConfirmingBulkDelete] = useState(false);
  async function bulkDelete() {
    setBulkDeleting(true);
    setMessage(null);
    try {
      await request("/api/admin/pd-days/bulk-delete", {
        method: "POST",
        body: JSON.stringify({ ids: [...selectedIds] }),
      });
      setMessage({ tone: "success", text: `Deleted ${selectedIds.size} PD days.` });
      setConfirmingBulkDelete(false);
      clear();
      load();
    } catch (cause) {
      setMessage({ tone: "error", text: errorMessage(cause) });
    } finally {
      setBulkDeleting(false);
    }
  }
  return (
    <div className="grid gap-6 min-[1800px]:grid-cols-[minmax(0,1fr)_380px]">
      <Card title="PD days" text={`${days?.length || 0} PD days`}>
        <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="relative min-w-0 flex-1 sm:max-w-sm">
            <Search
              size={16}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-content-disabled"
            />
            <input
              className={`${inputClass} pl-9`}
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                clear();
              }}
              placeholder="Search PD days"
              aria-label="Search PD days"
            />
          </div>
          <select
            className={`${inputClass} sm:w-40`}
            value={statusFilter}
            onChange={(event) => {
              setStatusFilter(event.target.value);
              clear();
            }}
            aria-label="Filter PD days by status"
          >
            <option value="all">All statuses</option>
            <option value="draft">Draft</option>
            <option value="published">Published</option>
            <option value="archived">Archived</option>
          </select>
        </div>
        <BulkActionsBar
          count={selectedIds.size}
          onDelete={() => setConfirmingBulkDelete(true)}
          onClear={clear}
          busy={bulkDeleting}
        />
        {listError ? (
          <ErrorState message={listError} />
        ) : days === null ? (
          <LoadingState message="Loading PD days…" />
        ) : !days.length ? (
          <EmptyState
            title="No PD days yet"
            message="Create one, then add its modules and their content."
          />
        ) : !filteredDays.length ? (
          <EmptyState title="No matching PD days" message="Try another search term." />
        ) : (
          <Table
            selection={selection}
            headers={[
              { label: "Order" },
              { label: "PD day" },
              { label: "Cohort", hide: "md" },
              { label: "Status" },
              { label: "Modules" },
              { label: "Items", hide: "sm" },
              { label: "Scheduled", hide: "sm" },
              { label: "Actions" },
            ]}
            rows={filteredDays.map((day) => [
              <span key="sequence" className="tabular-nums text-content-secondary">
                {day.sequence}
              </span>,
              <div key="title">
                <button
                  type="button"
                  className="text-left"
                  onClick={() => navigate(`/pd-days/${day.id}`)}
                >
                  <strong className="block font-semibold text-interactive hover:underline">
                    {day.title}
                  </strong>
                  {day.description && (
                    <span className="mt-0.5 block max-w-xs truncate text-xs text-content-secondary">
                      {day.description}
                    </span>
                  )}
                </button>
                <div className="mt-1.5">
                  <AttributionCell info={attribution[day.id]} />
                </div>
              </div>,
              day.cohort,
              <Badge key="status" tone={day.status === "published" ? "success" : "neutral"}>
                {day.status}
              </Badge>,
              day.module_count,
              day.item_count,
              day.session_count ? (
                `${day.session_count}×`
              ) : (
                <span key="unscheduled" className="text-content-secondary">
                  Not yet
                </span>
              ),
              <div key="actions" className="flex items-center gap-3">
                <button
                  type="button"
                  className="inline-flex items-center gap-1 text-content-secondary hover:text-interactive"
                  onClick={() => navigate(`/pd-days/${day.id}`)}
                  aria-label={`Edit ${day.title}`}
                >
                  <Pencil size={16} />
                </button>
                <button
                  type="button"
                  className="inline-flex items-center gap-1 text-content-secondary hover:text-interactive disabled:opacity-50"
                  onClick={() => {
                    setConfirmAction("archive");
                    setConfirmDay(day);
                  }}
                  disabled={deletingID === day.id}
                  aria-label={`Archive ${day.title}`}
                >
                  <Archive size={16} />
                </button>
                <button
                  type="button"
                  className="inline-flex items-center gap-1 text-content-secondary hover:text-danger disabled:opacity-50"
                  onClick={() => {
                    setConfirmAction("delete");
                    setConfirmDay(day);
                  }}
                  disabled={deletingID === day.id}
                  aria-label={`Delete ${day.title}`}
                >
                  {deletingID === day.id ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <Trash2 size={16} />
                  )}
                </button>
              </div>,
            ])}
          />
        )}
      </Card>
      <Card title="Create PD day" text="Add a PD day to a cohort, then add its modules.">
        <form className="grid gap-4 sm:grid-cols-2" onSubmit={submit}>
          <label className={`${labelClass} sm:col-span-2`}>
            Cohort
            <select className={inputClass} name="cohort_id" required>
              <option value="">Select cohort</option>
              {cohorts.map((cohort) => (
                <option key={cohort.id} value={cohort.id}>
                  {cohort.name}
                </option>
              ))}
            </select>
          </label>
          <div className="sm:col-span-2">
            <Field name="title" />
          </div>
          <div className="sm:col-span-2">
            <Field name="description" required={false} />
          </div>
          <label className={`${labelClass} sm:col-span-2`}>
            Title (Urdu, optional)
            <input className={inputClass} name="title_ur" dir="rtl" />
          </label>
          <label className={`${labelClass} sm:col-span-2`}>
            Status
            <select className={inputClass} name="status">
              <option>draft</option>
              <option>published</option>
            </select>
          </label>
          <button className={`${primaryButtonClass} sm:col-span-2`} disabled={busy}>
            {busy && <Loader2 size={16} className="animate-spin" />}Create
          </button>
        </form>
        {message && <InlineNotice tone={message.tone} text={message.text} />}
      </Card>
      <ConfirmModal
        open={!!confirmDay}
        title={confirmAction === "delete" ? "Delete PD day?" : "Archive PD day?"}
        message={
          confirmAction === "delete"
            ? `"${confirmDay?.title}", its modules and all their content will be permanently deleted, along with every scheduled session of it (and their attendance records). This cannot be undone.`
            : `"${confirmDay?.title}" and its modules will no longer be available to teachers.`
        }
        confirmLabel={confirmAction === "delete" ? "Delete" : "Archive"}
        busy={!!confirmDay && deletingID === confirmDay.id}
        onConfirm={() => confirmDay && remove(confirmDay, confirmAction)}
        onCancel={() => setConfirmDay(null)}
      />
      <ConfirmModal
        open={confirmingBulkDelete}
        title={`Delete ${selectedIds.size} PD days?`}
        message="These PD days, their modules and all their content will be permanently deleted, along with every scheduled session of them (and their attendance records). This cannot be undone."
        confirmLabel="Delete"
        busy={bulkDeleting}
        onConfirm={() => void bulkDelete()}
        onCancel={() => setConfirmingBulkDelete(false)}
      />
    </div>
  );
}
